import { useLocation } from 'react-router';
import SEO from './SEO';
import marketingSeo from '../data/marketing-seo.json';

function normalizePathname(path: string): string {
  if (!path || path === '/') return '/';
  const trimmed = path.endsWith('/') ? path.replace(/\/+$/, '') : path;
  return trimmed || '/';
}

const MARKETING = marketingSeo as Record<
  string,
  { title: string; description: string; canonical: string; jsonLd?: object | object[] }
>;

/**
 * Single Helmet for marketing URLs so head tags stay correct even if React Router
 * discovers other route modules while links are on screen.
 *
 * When `useLocation()` is briefly out of sync with the real URL, skip head updates
 * so Helmet does not apply `/` meta on deep links.
 *
 * Prerendered HTML is normalized again in `scripts/prerender.mjs` (Cheerio) so crawlers
 * always see a single canonical title/description set per route.
 */
export default function MarketingRouteSEO() {
  const loc = useLocation();
  const routerPath = normalizePathname(loc.pathname);
  const documentPath =
    typeof document !== 'undefined' ? normalizePathname(document.location.pathname) : routerPath;

  if (typeof document !== 'undefined' && routerPath !== documentPath) {
    return null;
  }

  const path = routerPath;
  const meta = MARKETING[path];
  if (!meta) return null;

  return (
    <SEO
      title={meta.title}
      description={meta.description}
      canonical={meta.canonical}
      jsonLd={meta.jsonLd}
    />
  );
}
